 <template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <div class="row align-items-center">
        <div class="col">
          <h6 class="mb-0">
            <font-awesome-icon icon="users" class="me-2" />
            Gestión de Usuarios
          </h6>
          <p class="text-sm text-muted mb-0 mt-1">Administra los usuarios del sistema</p>
        </div>
        <div class="col-auto">
          <button class="btn bg-gradient-success btn-sm mb-0" @click="$router.push('/users-create')">
            <font-awesome-icon icon="plus" class="me-1" />
            Crear Usuario
          </button>
        </div>
      </div>
    </div>
    <div class="card-body px-4">
      <div class="table-responsive">
        <!-- Controles de búsqueda y paginación -->
        <div class="row mb-3">
          <div class="col-md-3">
            <select name="per_page" id="per_page" v-model="pagination.limit" class="form-control form-control-sm" @change="reloadPaginate(1)">
              <option value="20">20 por página</option>
              <option value="50">50 por página</option>
              <option value="100">100 por página</option>
              <option value="200">200 por página</option>
            </select>
          </div>
          <div class="col-md-4 ms-md-auto">
            <div class="input-group input-group-sm">
              <span class="input-group-text">
                <font-awesome-icon icon="search" />
              </span>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Buscar por email o nombre..." 
                v-model="pagination.search" 
                @input="debouncedSearch"
              >
              <button v-if="pagination.search" class="btn btn-outline-secondary" type="button" @click="clearSearch">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="text-muted mt-2">Cargando usuarios...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger" role="alert">
          <font-awesome-icon icon="exclamation-triangle" class="me-2" />
          {{ error }}
          <button class="btn btn-sm btn-danger ms-3" @click="getData">
            <font-awesome-icon icon="sync" class="me-1" />
            Reintentar
          </button>
        </div>

        <!-- Empty State -->
        <div v-else-if="arrayUsers.length === 0" class="text-center py-4">
          <font-awesome-icon icon="inbox" size="3x" class="text-muted mb-3" />
          <p class="text-muted">No se encontraron usuarios</p>
          <button v-if="pagination.search" class="btn btn-sm btn-outline-primary" @click="clearSearch">
            Limpiar búsqueda
          </button>
        </div>

        <!-- Tabla de usuarios -->
        <div v-else>
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Usuario</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Email</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Rol</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha de Registro</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center" style="width: 100px;">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, key) in arrayUsers" :key="key">
                <td>
                  <div class="d-flex px-2 py-1">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-sm">{{ item.name }}</h6>
                    </div>
                  </div>
                </td>
                <td>
                  <p class="text-xs font-weight-bold mb-0">{{ item.email }}</p>
                </td>
                <td>
                  <span class="badge bg-secondary text-capitalize">{{ item.type }}</span>
                </td>
                <td>
                  <span 
                    class="badge text-xs"
                    :class="item.status ? 'bg-success' : 'bg-secondary'"
                  >
                    {{ item.status ? 'ACTIVO' : 'INACTIVO' }}
                  </span>
                </td>
                <td>
                  <p class="text-xs font-weight-bold mb-0">{{ $filters.formatDate(item.created_at) }}</p>
                </td>
                <td class="align-middle text-center">
                  <div class="d-flex gap-2 justify-content-center">
                    <button 
                      class="btn btn-primary btn-xs" 
                      @click="view(item)"
                      title="Ver detalles"
                    >
                      <font-awesome-icon icon="eye" />
                    </button>
                    <button 
                      v-if="item.type !== 'administrator'" 
                      class="btn btn-danger btn-xs" 
                      @click="remove(item)"
                      title="Eliminar usuario"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer con paginación -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div>
            <p class="text-xs font-weight-bold mb-0">
              Mostrando {{ arrayUsers.length }} de {{ pagination.totalRows }} usuarios
            </p>
          </div>
          <div>
            <v-pagination
              v-model="pagination.page"
              :pages="pagination.pages"
              :range-size="1"
              active-color="#82d616"
              @update:modelValue="reloadPaginate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from "@/utils/httpRequest";
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faUsers, faPlus, faSearch, faTimes, faEye, faTrash, faExclamationTriangle, faSync, faInbox } from '@fortawesome/free-solid-svg-icons';

library.add(faUsers, faPlus, faSearch, faTimes, faEye, faTrash, faExclamationTriangle, faSync, faInbox);

export default {
  name: "UsersIndex",
  components: {
    FontAwesomeIcon,
  },
  data: () => ({
    arrayUsers: [],
    pagination: {
      searchTimer: null,
      page: 1,
      pages: 0,
      limit: 20,
      search: '',
      totalRows: 0
    },
    loading: false,
    error: null
  }),
  mounted() {
    this.getData()
  },
  methods: {
    reloadPaginate(page) {
      this.pagination.page = page;
      this.getData();
    },
    
    debouncedSearch() {
      clearTimeout(this.pagination.searchTimer);
      this.pagination.searchTimer = setTimeout(() => {
        this.reloadPaginate(1);
      }, 400); // Reducido de 2000ms a 400ms
    },
    
    clearSearch() {
      this.pagination.search = '';
      this.reloadPaginate(1);
    },

    async getData() {
      this.loading = true;
      this.error = null;
      this.arrayUsers = [];
      
      try {
        const { data } = await get(`users/find-all?page=${this.pagination.page}&limit=${this.pagination.limit}&search=${encodeURIComponent(this.pagination.search)}`, null, true);
        
        this.arrayUsers = data.docs || [];
        this.pagination.totalRows = data.count || 0;
        this.pagination.page = data.page || 1;
        this.pagination.pages = data.pages || 0;
      } catch (err) {
        console.error('Error loading users:', err);
        this.error = 'No se pudo cargar la lista de usuarios. Por favor, intenta nuevamente.';
      } finally {
        this.loading = false;
      }
    },
    
    async remove(item) {
      if (item.type === 'administrator') {
        return;
      }
      
      try {
        const result = await this.$swal({
          title: '¿Eliminar usuario?',
          html: `¿Estás seguro de eliminar al usuario <strong>${item.name}</strong>?<br>Esta acción no se puede deshacer.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#ea0606',
          cancelButtonColor: '#6c757d',
          reverseButtons: true
        });

        if (result.isConfirmed) {
          await post("users/remove", { _id: item._id }, true);
          
          await this.$swal({
            title: '¡Eliminado!',
            html: `El usuario <strong>${item.name}</strong> ha sido eliminado exitosamente.`,
            icon: 'success',
            timer: 2500,
            showCancelButton: false,
            showConfirmButton: false,
          });
          
          await this.getData();
        }
      } catch (err) {
        console.error('Error deleting user:', err);
        this.$swal({
          title: 'Error',
          text: 'No se pudo eliminar el usuario. Por favor, intenta nuevamente.',
          icon: 'error',
          confirmButtonColor: '#ea0606'
        });
      }
    },
    
    view(item) {
      this.$router.push({ name: 'agents-view', query: { id: item._id } });
    }
  }
}
</script>

<style scoped lang="scss">
.card-header {
  h6 {
    font-weight: 600;
    color: #344767;
  }
  
  p {
    font-size: 0.875rem;
  }
}

.table {
  thead th {
    border-bottom: 1px solid #e9ecef;
    font-weight: 700;
    font-size: 0.65rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  
  tbody td {
    border-bottom: 1px solid #f0f2f5;
  }
}

.input-group-text {
  background-color: transparent;
  border-right: 0;
  border-color: #d2d6da;
}

.form-control:focus + .input-group .input-group-text {
  border-color: #82d616;
}

.alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  button {
    flex-shrink: 0;
  }
}

.btn-xs {
  padding: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  border: none;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.15s ease;
  
  svg {
    font-size: 0.85rem;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08);
  }
}

.btn-primary {
  background-image: linear-gradient(310deg, #7928ca 0%, #ff0080 100%) !important;
  color: #fff !important;
  
  &:hover {
    background-image: linear-gradient(310deg, #7928ca 0%, #ff0080 100%) !important;
    opacity: 0.85;
  }
}

.btn-danger {
  background-image: linear-gradient(310deg, #ea0606 0%, #ff667c 100%) !important;
  color: #fff !important;
  
  &:hover {
    background-image: linear-gradient(310deg, #ea0606 0%, #ff667c 100%) !important;
    opacity: 0.85;
  }
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  
  &:hover {
    transform: translateY(-1px);
    transition: all 0.15s ease;
  }
}

.badge {
  font-weight: 600;
  
  &.bg-success {
    background-color: #82d616 !important;
    color: #000 !important;
  }
  
  &.bg-secondary {
    background-color: #6c757d !important;
  }
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    transform: translateY(-1px);
    transition: all 0.15s ease;
  }
  
  &.btn-primary {
    background-color: #5e72e4;
    border-color: #5e72e4;
    color: white;
    
    &:hover {
      background-color: #4c63d2;
      border-color: #4c63d2;
    }
  }
  
  &.btn-danger {
    background-color: #ea0606;
    border-color: #ea0606;
    color: white;
    
    &:hover {
      background-color: #c70505;
      border-color: #c70505;
    }
  }
}

:deep(.v-pagination) {
  .page-item {
    .page-link {
      border: 1px solid #dee2e6;
      color: #8392ab;
      font-size: 0.875rem;
      padding: 0.5rem 0.75rem;
      
      &:hover {
        background-color: #f8f9fa;
        color: #344767;
      }
    }
    
    &.active .page-link {
      background-color: #82d616;
      border-color: #82d616;
      color: #000;
      font-weight: 600;
    }
    
    &:first-child .page-link,
    &:last-child .page-link {
      border-radius: 0.5rem;
    }
  }
}
</style>
